import { Add, ArrowDropDown, ExpandMore, InfoOutline, MoreVert } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  ButtonGroup,
  Divider,
  Fab,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import './QuoteDetails.css'
import { quoteType, vendorQuoteType, salesforceOpportunityType, quoteLineType } from '../types'
import { useNavigate, useParams } from 'react-router-dom'
import { dataContext } from '..'
import axios from 'axios'
import dayjs from 'dayjs'
import { contractNumbers, contractVehicles, govPayTerms, quoteStatuses } from '../lists'
import { DatePicker, CRMSearch, CurrencyInput } from '../helper'
import {
  CloneDialog,
  DeleteDialog,
  GenerateQuoteDialog,
  NewOpportunityDialog,
  NewVersionDialog,
  SyncQuoteDialog,
  UploadVendorTemplateDialog,
} from './QuoteDetailsOptionsDialogs'
import ErrorScreen from '../Misc/ErrorScreen'

function QuoteDetails() {
  const { quoteId } = useParams()
  const { user, repCodes } = useContext(dataContext)
  const navigate = useNavigate()

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null)
  const [activeTab, setActiveTab] = useState('details')
  const [quote, setQuote] = useState<quoteType>({} as quoteType)
  const [quoteLines, setQuoteLines] = useState<(quoteLineType & { children: quoteLineType[] })[]>([])
  const [versions, setVersions] = useState<
    { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]
  >([])
  const [crmOpportunity, setCrmOpportunity] = useState<null | salesforceOpportunityType>(null)
  const [editMainQuoteInfo, setEditMainQuoteInfo] = useState(false)
  const [editTotals, setEditTotals] = useState(false)
  const [editAdditionalInfo, setEditAdditionalInfo] = useState(false)
  const [editOpportunityInfo, setEditOpportunityInfo] = useState(false)
  const [editQuoteCopy, setEditQuoteCopy] = useState<quoteType>({} as quoteType)
  const [selectedOpp, setSelectedOpp] = useState<null | { id: string; label: string }>(null)
  const [selectedAccount, setSelectedAccount] = useState<null | { id: string; label: string }>(null)
  const [selectedContact, setSelectedContact] = useState<null | { id: string; label: string }>(null)
  const [selectedIsr, setSelectedIsr] = useState<null | string>(null)
  const [quoteLoading, setQuoteLoading] = useState(true)
  const [vendorQuotes, setVendorQuotes] = useState<vendorQuoteType[]>([])
  const [openDialogs, setOpenDialogs] = useState<{
    generateQuote: boolean
    delete: boolean
    newVersion: boolean
    clone: boolean
    syncSalesforceQuote: boolean
    newOpportunity: boolean
    vendorTemplate: boolean
  }>({
    generateQuote: false,
    delete: false,
    newVersion: false,
    clone: false,
    syncSalesforceQuote: false,
    newOpportunity: false,
    vendorTemplate: false,
  })
  const [quoteLoadError, setQuoteLoadError] = useState(false)
  const [saveError, setSaveError] = useState<null | string>(null)
  const [saveOpportunityError, setSaveOpportunityError] = useState(false)

  useEffect(() => {
    if (!user.email || !quoteId) {
      return
    }

    setQuoteLoading(true)
    setQuoteLoadError(false)
    axios
      .get(`/quote/${quoteId}`)
      .then(res => {
        setQuote(res.data.quote)
        setEditQuoteCopy(res.data.quote)
        setVersions(res.data.versions)
        setCrmOpportunity(res.data.crmOpportunity)
        setVendorQuotes(res.data.vendorQuotes)
        setSelectedOpp(
          res.data.crmOpportunity ? { id: res.data.crmOpportunity.Id, label: res.data.crmOpportunity.Name } : null,
        )
        setSelectedAccount(
          res.data.crmOpportunity?.Account
            ? { id: res.data.crmOpportunity.Account.Id, label: res.data.crmOpportunity.Account.Name }
            : null,
        )
        setSelectedContact(
          res.data.crmOpportunity?.Contact
            ? { id: res.data.crmOpportunity.Contact.Id, label: res.data.crmOpportunity.Contact.Name }
            : null,
        )
        setSelectedIsr(res.data.crmOpportunity?.Owner.Email)

        setQuoteLines(
          res.data.lineItems
            .filter((line: quoteLineType) => line.LineNumber === -1 || line.Bundle === null)
            .map((line: quoteLineType) => ({
              ...line,
              children: res.data.lineItems
                .filter(
                  (child: quoteLineType) =>
                    child.Bundle === line.Bundle && child.LineNumber !== -1 && child.Bundle !== null,
                )
                .sort((a: quoteLineType, b: quoteLineType) => a.LineNumber - b.LineNumber),
            }))
            .sort(
              (a: quoteLineType & { children: quoteLineType[] }, b: quoteLineType & { children: quoteLineType[] }) => {
                const aNum =
                  a.LineNumber === -1
                    ? a.children.reduce(
                        (min: number, child: quoteLineType) => Math.min(min, child.LineNumber),
                        Infinity,
                      )
                    : a.LineNumber
                const bNum =
                  b.LineNumber === -1
                    ? b.children.reduce(
                        (min: number, child: quoteLineType) => Math.min(min, child.LineNumber),
                        Infinity,
                      )
                    : b.LineNumber
                return aNum - bNum
              },
            ),
        )
      })
      .catch(err => {
        setQuoteLoadError(true)
        console.error(err)
      })
      .finally(() => {
        setQuoteLoading(false)
      })
  }, [user.email, quoteId])

  const handleSyncSalesforceQuote = (contactId?: string | null) => {
    return axios.post(`/syncSalesforceQuote/${quoteId}`, {
      quote: {
        OpportunityId: selectedOpp?.id,
        I_Quote__c: `${quote.QuoteNumber}v${quote.Version}`,
        Name: `${quote.QuoteNumber}v${quote.Version}`,
        RFQ__c: quote.RFQNumber,
        RFQ_Due_Date__c: quote.RFQDueDate,
        Contract_Vehicle__c: quote.ContractVehicle,
        Contract_Number__c: quote.ContractNumber,
        Quote_Date__c: quote.QuoteDate,
        ExpirationDate: quote.ExpirationDate,
        Status: quote.Status,
        ContactId: contactId === undefined ? selectedContact?.id : contactId,
      },
      quoteLines: quoteLines.map(line => ({
        IMPRES_LineNum__c: line.LineNumber,
        Distributor__c: line.Provider,
        Manufacturer__c: line.Manufacturer,
        Mfr_PartNum__c: line.MfrPartNumber,
        Display_PartNum__c: line.DisplayPartNumber,
        Description__c: line.Description,
        QTY__c: line.Qty ?? 0,
        Quantity: 1,
        Unit_Cost__c: line.UnitCost,
        Ext_Cost__c: line.ExtCost,
        OEM_Code__c: line.OEMCode,
        Validation__c: line.CLIN,
        UnitPrice: line.UnitPrice ?? 0,
        Product_Type__c: line.ProductType,
        Start_Date__c: line.StartDate,
        End_Date__c: line.EndDate,
        Service_Tag__c: line.ServiceTag,
        SerialNum__c: line.LicenseSN,
        Notes__c: line.Notes,
        TAA__c: line.TAA,
        Country_of_Origin__c: line.CountryOfOrigin,
        Contract_Ceiling_Price__c: line.ContractCeilingPrice,
        Contract_Catalog__c: line.ContractCatalog,
        ContractNum__c: line.ContractNumber,
        Unit_of_Measure__c: line.UnitOfMeasure,
        IMPRES_List_Price__c: line.MSRP,
      })),
    })
  }

  const handleEditQuote = () => {
    setSaveError(null)

    Promise.all([
      axios
        .post(`/editQuote/${quoteId}`, {
          quote: {
            ContractVehicle: editQuoteCopy.ContractVehicle,
            ContractNumber: editQuoteCopy.ContractNumber,
            ExpirationDate: editQuoteCopy.ExpirationDate,
            Status: editQuoteCopy.Status,
            RFQNumber: editQuoteCopy.RFQNumber,
            RFQDueDate: editQuoteCopy.RFQDueDate,
            QuoteDate: editQuoteCopy.QuoteDate,
            QuoteDescription: editQuoteCopy.QuoteDescription,
            GovPayTerms: editQuoteCopy.GovPayTerms,
            Name: editQuoteCopy.Name,
            ...(editQuoteCopy.CRMOpportunity ? { CRMOpportunity: editQuoteCopy.CRMOpportunity } : {}),
          },
        })
        .catch(err => {
          setSaveError(
            'Main Quote Information failed to save. Please try again or contact support if the problem persists.',
          )
          return Promise.reject(err)
        }),
      selectedAccount?.id !== crmOpportunity?.Account?.Id ||
      selectedContact?.id !== crmOpportunity?.Contact?.Id ||
      selectedIsr !== crmOpportunity?.Owner.Email
        ? axios
            .post(`/updateOpportunity/${editQuoteCopy.CRMOpportunity}`, {
              opportunity: {
                AccountId: selectedAccount?.id,
                Owner: {
                  Email: selectedIsr,
                },
              },
              quoteId: quote.QuoteId,
              quote: {
                ContactId: selectedContact?.id,
              },
            })
            .catch(err => {
              setSaveError(
                'Opportunity information failed to update. Please try again or contact support if the problem persists.',
              )
              return Promise.reject(err)
            })
        : Promise.resolve(null),
    ])
      .then(async ([quoteRes, oppRes]) => {
        setQuote(editQuoteCopy)
        setEditMainQuoteInfo(false)
        if (oppRes) {
          if (oppRes.data.Account.Id !== selectedAccount?.id) {
            setSelectedContact(null)
          }
          setCrmOpportunity(oppRes.data)
          setSelectedAccount({ id: oppRes.data.Account.Id, label: oppRes.data.Account.Name })
          setSelectedIsr(oppRes.data.Owner.Email)

          if (selectedOpp?.id !== crmOpportunity?.Id) {
            handleSyncSalesforceQuote(oppRes.data.Acount.Id === selectedAccount?.id ? undefined : null).catch(err => {
              console.error(err)
            })
          }
        } else if (quoteRes.data) {
          if (quoteRes.data.Account.Id !== selectedAccount?.id) {
            setSelectedContact(null)
          }
          setCrmOpportunity(quoteRes.data)
          setSelectedAccount({ id: quoteRes.data.Account.Id, label: quoteRes.data.Account.Name })
          setSelectedIsr(quoteRes.data.Owner.Email)

          if (selectedOpp?.id !== crmOpportunity?.Id) {
            handleSyncSalesforceQuote(quoteRes.data.Acount.Id === selectedAccount?.id ? undefined : null).catch(err => {
              console.error(err)
            })
          }
        }
      })
      .catch(err => {
        console.error(err)
      })
  }

  const handleEditTotals = () => {
    setSaveError(null)

    axios
      .post(`/editQuote/${quoteId}`, {
        quote: {
          Tax: editQuoteCopy.Tax,
          ShippingHandling: editQuoteCopy.ShippingHandling,
          GrandTotal: quote.Subtotal + editQuoteCopy.Tax + editQuoteCopy.ShippingHandling,
        },
      })
      .then(() => {
        setQuote({ ...editQuoteCopy, GrandTotal: quote.Subtotal + editQuoteCopy.Tax + editQuoteCopy.ShippingHandling })
        setEditTotals(false)
      })
      .catch(err => {
        console.error(err)
        setSaveError('Totals failed to save. Please try again or contact support if the problem persists.')
      })
  }

  const handleEditAdditionalInfo = () => {
    setSaveError(null)

    axios
      .post(`/editQuote/${quoteId}`, {
        quote: {
          RFQNumber: editQuoteCopy.RFQNumber,
          RFQDueDate: editQuoteCopy.RFQDueDate,
          ContractNumber: editQuoteCopy.ContractNumber,
        },
      })
      .then(() => {
        setQuote(editQuoteCopy)
        setEditAdditionalInfo(false)
      })
      .catch(err => {
        console.error(err)
        setSaveError(
          'Additional Quote Information failed to save. Please try again or contact support if the problem persists.',
        )
      })
  }

  const handleEditOpportunityInfo = () => {
    axios
      .post(`/editQuote/${quoteId}`, {
        quote: {
          CRMOpportunity: selectedOpp?.id,
        },
      })
      .then(res => {
        if (res.data.Account.Id !== selectedAccount?.id) {
          setSelectedContact(null)
        }
        setQuote({ ...quote, CRMOpportunity: selectedOpp?.id ?? null })
        setCrmOpportunity(res.data)
        setSelectedAccount({ id: res.data.Account.Id, label: res.data.Account.Name })
        setSelectedIsr(res.data.Owner.Email)
        setEditOpportunityInfo(false)

        handleSyncSalesforceQuote(res.data.Account.Id === selectedAccount?.id ? undefined : null).catch(err => {
          console.error(err)
        })
      })
      .catch(err => {
        setSaveOpportunityError(true)
        console.error(err)
      })
  }

  if (quoteLoadError) {
    return (
      <ErrorScreen
        className="quote-details-error-screen"
        title="Error Loading Quote"
        message="An error occurred while loading the quote. Please try again or contact support if the problem persists."
      />
    )
  }

  return (
    <div className="quote-details">
      <div className="quote-details-row-1">
        <div>
          <div>
            <Typography variant="body2" color="text.secondary">
              IMPRES Quote #:
            </Typography>
            {quoteLoading ? <Skeleton variant="text" width={100} /> : <Typography>{quote.QuoteNumber}</Typography>}
          </div>
          <Divider orientation="vertical" />
          <div>
            <Typography variant="body2" color="text.secondary">
              RFQ #:
            </Typography>
            {quoteLoading ? <Skeleton variant="text" width={100} /> : <Typography>{quote.RFQNumber}</Typography>}
          </div>
          <Divider orientation="vertical" />
          <div>
            <Typography variant="body2" color="text.secondary">
              Contract Vehicle:
            </Typography>
            {quoteLoading ? <Skeleton variant="text" width={100} /> : <Typography>{quote.ContractVehicle}</Typography>}
          </div>
          <Divider orientation="vertical" />
          <div>
            <Typography variant="body2" color="text.secondary">
              Version:
            </Typography>
            {quoteLoading ? (
              <Skeleton variant="text" width={100} />
            ) : (
              <TextField
                select
                fullWidth
                label="Version"
                size="small"
                value={quote.QuoteId}
                onChange={e => navigate(`/quote/${e.target.value}`)}
              >
                {versions.map(version => (
                  <MenuItem value={version.QuoteId} key={version.QuoteId}>
                    v{version.Version} {version.Name ? `- ${version.Name}` : ''} (
                    {dayjs(version.CreatedDate).format('MM/DD/YYYY hh:mm A')})
                  </MenuItem>
                ))}
              </TextField>
            )}
          </div>
        </div>
        <div>
          <ButtonGroup variant="outlined">
            <Button onClick={() => setOpenDialogs(prev => ({ ...prev, vendorTemplate: true }))}>
              Upload Vendor Template
            </Button>
            <Button>Upload Vendor Quote</Button>
          </ButtonGroup>
          <UploadVendorTemplateDialog
            open={openDialogs.vendorTemplate}
            quoteId={quoteId ?? ''}
            onClose={() => setOpenDialogs(prev => ({ ...prev, vendorTemplate: false }))}
            setVendorQuotes={setVendorQuotes}
            setLineItems={setQuoteLines}
            setQuote={setQuote}
          />
          <Button
            variant="contained"
            startIcon={<MoreVert />}
            endIcon={<ArrowDropDown />}
            onClick={e => setMoreAnchorEl(e.currentTarget)}
          />
          <Menu
            open={!!moreAnchorEl}
            onClose={() => setMoreAnchorEl(null)}
            anchorEl={moreAnchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              onClick={() => {
                setOpenDialogs(prev => ({ ...prev, delete: true }))
                setMoreAnchorEl(null)
              }}
            >
              Delete Quote
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenDialogs(prev => ({ ...prev, newVersion: true }))
                setMoreAnchorEl(null)
              }}
            >
              New Version
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenDialogs(prev => ({ ...prev, clone: true }))
                setMoreAnchorEl(null)
              }}
            >
              Clone
            </MenuItem>
            <MenuItem>Print Quote</MenuItem>
            <MenuItem
              onClick={() => {
                setOpenDialogs(prev => ({ ...prev, syncSalesforceQuote: true }))
                setMoreAnchorEl(null)
              }}
            >
              Sync Quote with Salesforce
            </MenuItem>
            <MenuItem>Create Sales Order</MenuItem>
          </Menu>
          <DeleteDialog
            open={openDialogs.delete}
            onClose={() => setOpenDialogs(prev => ({ ...prev, delete: false }))}
            quoteId={quoteId ?? ''}
          />
          <NewVersionDialog
            open={openDialogs.newVersion}
            onClose={() => setOpenDialogs(prev => ({ ...prev, newVersion: false }))}
            quoteId={quoteId ?? ''}
            nextVersion={versions.length + 1}
            contactId={selectedContact?.id}
          />
          <CloneDialog
            open={openDialogs.clone}
            onClose={() => setOpenDialogs(prev => ({ ...prev, clone: false }))}
            quoteId={quoteId ?? ''}
            contactId={selectedContact?.id}
          />
          <SyncQuoteDialog
            open={openDialogs.syncSalesforceQuote}
            onClose={() => setOpenDialogs(prev => ({ ...prev, syncSalesforceQuote: false }))}
            handleSync={handleSyncSalesforceQuote}
          />
        </div>
      </div>
      <Divider flexItem />
      <Stepper
        alternativeLabel
        activeStep={
          quote.Status === 'Obsolete'
            ? 0
            : quote.Status === 'Draft'
            ? 1
            : quote.Status === 'Approved'
            ? 2
            : quote.Status === 'Sent to Customer'
            ? 3
            : quote.Status === 'Closed Lost'
            ? 4
            : 5
        }
      >
        <Step>
          <StepLabel>Obsolete</StepLabel>
        </Step>
        <Step>
          <StepLabel>Draft</StepLabel>
        </Step>
        <Step>
          <StepLabel>Approved</StepLabel>
        </Step>
        <Step>
          <StepLabel>Sent to Customer</StepLabel>
        </Step>
        <Step>
          <StepLabel>Closed Lost</StepLabel>
        </Step>
        <Step>
          <StepLabel>Closed Won</StepLabel>
        </Step>
      </Stepper>
      <Divider flexItem />
      <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)} id="quote-details-tabs">
        <Tab value="vendorquotes" label="Vendor Quotes" />
        <Tab value="details" label="Details and Line Items" />
        <Tab value="opportunity" label="Opportunity Data" />
        <Tab value="quotepdfs" label="Quote PDFs" />
      </Tabs>
      {activeTab === 'vendorquotes' && (
        <Accordion disableGutters defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>Vendor Quotes</AccordionSummary>
          <AccordionDetails>
            <Table className="vendor-quote-table">
              <TableHead>
                <TableCell>Vendor Name</TableCell>
                <TableCell>Vendor Quote #</TableCell>
                <TableCell>Expiration Date</TableCell>
                <TableCell>Total Amount</TableCell>
              </TableHead>
              <TableBody>
                {quoteLoading ? (
                  <TableRow>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" />
                    </TableCell>
                  </TableRow>
                ) : vendorQuotes.length > 0 ? (
                  vendorQuotes.map(vendorQuote => (
                    <TableRow key={vendorQuote.VendorQuoteId}>
                      <TableCell>{vendorQuote.VendorName ?? '-'}</TableCell>
                      <TableCell>{vendorQuote.VendorQuoteNumber ?? '-'}</TableCell>
                      <TableCell>
                        {vendorQuote.ExpirationDate ? dayjs(vendorQuote.ExpirationDate).format('MM/DD/YYYY') : '-'}
                      </TableCell>
                      <TableCell>
                        {vendorQuote.TotalAmount
                          ? vendorQuote.TotalAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No vendor quotes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      )}
      {activeTab === 'details' && (
        <div className="quote-details-details-tab-container">
          <Accordion disableGutters defaultExpanded id="quote-details-main-quote-info">
            <AccordionSummary expandIcon={<ExpandMore />}>Main Quote Information</AccordionSummary>
            <AccordionDetails>
              <div className="quote-details-main-quote-info">
                <Grid container spacing="17px" width="100%">
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        select
                        label="Quote Owner *"
                        size="small"
                        disabled={!editMainQuoteInfo}
                        value={selectedIsr}
                        onChange={e => setSelectedIsr(e.target.value)}
                      >
                        {repCodes.map(repCode => (
                          <MenuItem value={repCode.IsrEmail} key={repCode.IsrEmail}>
                            {repCode.IsrName}
                          </MenuItem>
                        ))}
                        {!repCodes.map(repCode => repCode.IsrEmail).includes(selectedIsr ?? '') && (
                          <MenuItem value={selectedIsr ?? ''} key={selectedIsr ?? ''}>
                            {selectedIsr}
                          </MenuItem>
                        )}
                      </TextField>
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        disabled={!editMainQuoteInfo}
                        label="Quote Name"
                        size="small"
                        value={editQuoteCopy.Name}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, Name: e.target.value }))}
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        disabled={!editMainQuoteInfo}
                        label="Description"
                        size="small"
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="start">
                                <Tooltip title={editQuoteCopy.QuoteDescription}>
                                  <InfoOutline />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          },
                        }}
                        value={editQuoteCopy.QuoteDescription}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, QuoteDescription: e.target.value }))}
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <CRMSearch
                        object="Opportunity"
                        label="Opportunity *"
                        disabled={!editMainQuoteInfo}
                        value={selectedOpp}
                        onChange={(e, value) => {
                          setSelectedOpp(value)
                          setEditQuoteCopy(prev => ({ ...prev, CRMOpportunity: value?.id ?? null }))
                        }}
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        select
                        disabled={!editMainQuoteInfo}
                        label="Contract Vehicle"
                        size="small"
                        value={editQuoteCopy.ContractVehicle}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, ContractVehicle: e.target.value }))}
                      >
                        {contractVehicles.map(vehicle => (
                          <MenuItem value={vehicle} key={vehicle}>
                            {vehicle}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <CRMSearch
                        object="Account"
                        value={selectedAccount}
                        onChange={(e, value) => setSelectedAccount(value)}
                        label="Account Name *"
                        disabled={!editMainQuoteInfo}
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <DatePicker
                        fullWidth
                        disabled={!editMainQuoteInfo}
                        label="Expiration Date"
                        size="small"
                        value={editQuoteCopy.ExpirationDate ? dayjs(editQuoteCopy.ExpirationDate) : null}
                        onChange={e =>
                          setEditQuoteCopy({ ...editQuoteCopy, ExpirationDate: e ? dayjs(e).toDate() : null })
                        }
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <CRMSearch
                        object="Contact"
                        accountId={selectedAccount?.id}
                        value={selectedContact}
                        onChange={(e, value) => setSelectedContact(value)}
                        label="Contact Name *"
                        disabled={!editMainQuoteInfo}
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        select
                        disabled={!editMainQuoteInfo}
                        label="Terms"
                        size="small"
                        value={editQuoteCopy.GovPayTerms}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, GovPayTerms: e.target.value }))}
                      >
                        {govPayTerms.map(term => (
                          <MenuItem value={term} key={term}>
                            {term}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        select
                        disabled={!editMainQuoteInfo}
                        label="Status"
                        size="small"
                        value={editQuoteCopy.Status}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, Status: e.target.value }))}
                      >
                        {quoteStatuses.map(status => (
                          <MenuItem value={status} key={status}>
                            {status}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <DatePicker
                        fullWidth
                        disabled={!editMainQuoteInfo}
                        label="Quote Date"
                        size="small"
                        value={editQuoteCopy.QuoteDate ? dayjs(editQuoteCopy.QuoteDate) : null}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, QuoteDate: e ? dayjs(e).toDate() : null }))}
                      />
                    )}
                  </Grid>
                  <Grid size={6}>
                    {quoteLoading ? (
                      <Skeleton variant="rectangular" width="100%" height={40} />
                    ) : (
                      <TextField
                        fullWidth
                        select
                        label="Rep Code"
                        size="small"
                        disabled={!editMainQuoteInfo}
                        value={repCodes.find(repCode => repCode.RepCode === editQuoteCopy.RepCode)?.RepCode ?? ''}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, RepCode: e.target.value }))}
                      >
                        {repCodes
                          .filter(repCode => repCode.IsrEmail === selectedIsr)
                          .map(repCode => (
                            <MenuItem value={repCode.RepCode} key={repCode.RepCode}>
                              {repCode.RepCode}{' '}
                              {[repCode.IsrName, repCode.AeName, repCode.EngineerName]
                                .filter(name => !!name)
                                .join(', ')}
                            </MenuItem>
                          ))}
                      </TextField>
                    )}
                  </Grid>
                </Grid>
                {quoteLoading ? (
                  <Skeleton variant="rectangular" width="64px" height="40px" />
                ) : editMainQuoteInfo ? (
                  <div className="button-container">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditMainQuoteInfo(false)
                        setEditQuoteCopy(quote)
                        setSelectedOpp(crmOpportunity ? { id: crmOpportunity.Id, label: crmOpportunity.Name } : null)
                        setSelectedAccount(
                          crmOpportunity?.Account
                            ? { id: crmOpportunity.Account.Id, label: crmOpportunity.Account.Name }
                            : null,
                        )
                        setSelectedContact(
                          crmOpportunity?.Contact
                            ? { id: crmOpportunity.Contact.Id, label: crmOpportunity.Contact.Name }
                            : null,
                        )
                        setSelectedIsr(crmOpportunity?.Owner.Email ?? null)
                      }}
                    >
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleEditQuote}>
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button variant="contained" onClick={() => setEditMainQuoteInfo(true)}>
                    Edit
                  </Button>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
          <TableContainer className="quote-details-line-items">
            <div>
              <Typography variant="h6"> Quote Line Items</Typography>
              <Button variant="contained" onClick={() => navigate(`/quote/${quoteId}/lines`)}>
                Edit Line Items
              </Button>
            </div>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Mfr Part #</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Distributor</TableCell>
                  <TableCell>Manufacturer</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit Cost</TableCell>
                  <TableCell align="right">Ext Cost</TableCell>
                  <TableCell align="right">Ext Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quoteLoading
                  ? Array.from({ length: 10 }).map((_, index) => (
                      <TableRow key={index}>
                        {Array.from({ length: 9 }).map((_, index) => (
                          <TableCell key={index}>
                            <Skeleton variant="text" />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  : quoteLines
                      .filter(
                        bundle =>
                          bundle.LineNumber <= 10 &&
                          (bundle.LineNumber !== -1 || bundle.children.some(child => child.LineNumber <= 10)),
                      )
                      .map((bundle, index) => (
                        <>
                          <TableRow key={index} className={bundle.LineNumber === -1 ? 'bundle-header' : 'no-bundle'}>
                            <TableCell>{bundle.LineNumber === -1 ? '' : bundle.LineNumber}</TableCell>
                            <TableCell>{bundle.MfrPartNumber}</TableCell>
                            <TableCell>{bundle.Description}</TableCell>
                            <TableCell>{bundle.Provider}</TableCell>
                            <TableCell>{bundle.Manufacturer}</TableCell>
                            <TableCell align="right">{bundle.Qty}</TableCell>
                            <TableCell align="right">
                              {bundle.UnitCost !== null
                                ? `${bundle.UnitCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {bundle.ExtCost !== null
                                ? `${bundle.ExtCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {bundle.ExtPrice !== null
                                ? `${bundle.ExtPrice.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
                                : '-'}
                            </TableCell>
                          </TableRow>
                          {bundle.children
                            .filter(child => child.LineNumber <= 10)
                            .map((child, index) => (
                              <TableRow
                                key={index}
                                className={
                                  index === bundle.children.filter(child => child.LineNumber <= 10).length - 1
                                    ? 'child-row-last'
                                    : undefined
                                }
                              >
                                <TableCell>{child.LineNumber}</TableCell>
                                <TableCell>{child.MfrPartNumber}</TableCell>
                                <TableCell>{child.Description}</TableCell>
                                <TableCell>{child.Provider}</TableCell>
                                <TableCell>{child.Manufacturer}</TableCell>
                                <TableCell align="right">{child.Qty}</TableCell>
                                <TableCell align="right">
                                  {child.UnitCost !== null
                                    ? `${child.UnitCost.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                      })}`
                                    : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {child.ExtCost !== null
                                    ? `${child.ExtCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
                                    : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {child.ExtPrice !== null
                                    ? `${child.ExtPrice.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                      })}`
                                    : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                        </>
                      ))}
              </TableBody>
            </Table>
            <Button variant="contained" onClick={() => navigate(`/quote/${quoteId}/lines`)}>
              View All
            </Button>
          </TableContainer>
          <Accordion disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>Totals</AccordionSummary>
            <AccordionDetails>
              <div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Total Partner Fees</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.PartnerFees.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Total Contract Fees</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.ContractFees.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Total Fees</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.TotalFees.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Total Cost</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.TotalCost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Gross Profit ($)</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.GrossProfit.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Gross Profit (%)</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{`${quote.GrossProfitPercentage * 100}%`}</Typography>
                    )}
                  </div>
                </div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Subtotal</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.Subtotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Tax</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <CurrencyInput
                        label="Tax"
                        size="small"
                        value={editQuoteCopy.Tax}
                        disabled={!editTotals}
                        onChange={value => setEditQuoteCopy(prev => ({ ...prev, Tax: value ?? 0 }))}
                      />
                    )}
                  </div>
                  <div>
                    <Typography>Shipping and Handling</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <CurrencyInput
                        label="Shipping and Handling"
                        size="small"
                        value={editQuoteCopy.ShippingHandling}
                        disabled={!editTotals}
                        onChange={value => setEditQuoteCopy(prev => ({ ...prev, ShippingHandling: value ?? 0 }))}
                      />
                    )}
                  </div>
                  <div>
                    <Typography>Grand Total</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {`${quote.GrandTotal.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
              {quoteLoading ? (
                <Skeleton variant="rectangular" width="64px" height="40px" />
              ) : editTotals ? (
                <div>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditTotals(false)
                      setEditQuoteCopy({ ...quote, Tax: quote.Tax, ShippingHandling: quote.ShippingHandling })
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleEditTotals}>
                    Save
                  </Button>
                </div>
              ) : (
                <Button variant="contained" onClick={() => setEditTotals(true)}>
                  Edit
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>Additional Quote Information</AccordionSummary>
            <AccordionDetails>
              <div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>RFQ Due Date</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <DatePicker
                        label="RFQ Due Date"
                        size="small"
                        disabled={!editAdditionalInfo}
                        value={editQuoteCopy.RFQDueDate ? dayjs(editQuoteCopy.RFQDueDate) : null}
                        onChange={e =>
                          setEditQuoteCopy(prev => ({ ...prev, RFQDueDate: e ? dayjs(e).toDate() : null }))
                        }
                      />
                    )}
                  </div>
                  <div>
                    <Typography>RFQ #</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <TextField
                        label="RFQ #"
                        size="small"
                        defaultValue={quote.RFQNumber}
                        disabled={!editAdditionalInfo}
                        value={editQuoteCopy.RFQNumber}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, RFQNumber: e.target.value }))}
                      />
                    )}
                  </div>
                </div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Contract #</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <TextField
                        select
                        label="Contract #"
                        size="small"
                        defaultValue={quote.ContractNumber}
                        disabled={!editAdditionalInfo}
                        value={editQuoteCopy.ContractNumber}
                        onChange={e => setEditQuoteCopy(prev => ({ ...prev, ContractNumber: e.target.value }))}
                      >
                        {contractNumbers.map(contractNumber => (
                          <MenuItem value={contractNumber} key={contractNumber}>
                            {contractNumber}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </div>
                </div>
              </div>
              {quoteLoading ? (
                <Skeleton variant="rectangular" width="64px" height="40px" />
              ) : editAdditionalInfo ? (
                <div>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditAdditionalInfo(false)
                      setEditQuoteCopy({
                        ...quote,
                        RFQNumber: quote.RFQNumber,
                        RFQDueDate: quote.RFQDueDate,
                        ContractNumber: quote.ContractNumber,
                      })
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleEditAdditionalInfo}>
                    Save
                  </Button>
                </div>
              ) : (
                <Button variant="contained" onClick={() => setEditAdditionalInfo(true)}>
                  Edit
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>Prepared For</AccordionSummary>
            <AccordionDetails>
              <div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Contact Name</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Contact?.Name ?? '-'}</Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Phone</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Contact?.Phone ?? '-'}</Typography>
                    )}
                  </div>
                  <div>
                    <Typography>Email</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Contact?.Email ?? '-'}</Typography>
                    )}
                  </div>
                </div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Contact Address</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {crmOpportunity?.Contact?.MailingAddress ? (
                          <>
                            {crmOpportunity.Contact.MailingAddress.street},<br />
                            {crmOpportunity.Contact.MailingAddress.city}, {crmOpportunity.Contact.MailingAddress.state}
                            <br />
                            {crmOpportunity.Contact.MailingAddress.postalCode}
                          </>
                        ) : (
                          '-'
                        )}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>Bill To Info</AccordionSummary>
            <AccordionDetails>
              <div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Account Name</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Account?.Name ?? '-'}</Typography>
                    )}
                  </div>
                </div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Billing Address</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>
                        {crmOpportunity?.Account?.BillingAddress ? (
                          <>
                            {crmOpportunity.Account.BillingAddress.street},<br />
                            {crmOpportunity.Account.BillingAddress.city}, {crmOpportunity.Account.BillingAddress.state}
                            <br />
                            {crmOpportunity.Account.BillingAddress.postalCode}
                          </>
                        ) : (
                          '-'
                        )}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>Seller Data</AccordionSummary>
            <AccordionDetails>
              <div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Inside Sales Rep</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Owner.Name ?? '-'}</Typography>
                    )}
                  </div>
                  <div>
                    <Typography>ISR Phone</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Owner.Phone ?? '-'}</Typography>
                    )}
                  </div>
                  <div>
                    <Typography>ISR Email</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Owner.Email ?? '-'}</Typography>
                    )}
                  </div>
                </div>
                <div className="quote-details-additional-info-panel">
                  <div>
                    <Typography>Account Executive</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Account?.Owner.Name ?? '-'}</Typography>
                    )}
                  </div>
                  <div>
                    <Typography>AE Phone</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Account?.Owner.Phone ?? '-'}</Typography>
                    )}
                  </div>
                  <div>
                    <Typography>AE Email</Typography>
                    {quoteLoading ? (
                      <Skeleton variant="text" width="10%" />
                    ) : (
                      <Typography>{crmOpportunity?.Account?.Owner.Email ?? '-'}</Typography>
                    )}
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <Snackbar open={!!saveError}>
            <Alert severity="error" variant="filled" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          </Snackbar>
        </div>
      )}
      {activeTab === 'opportunity' && (
        <>
          <Accordion disableGutters defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />}>Data</AccordionSummary>
            <AccordionDetails>
              <div className="quote-details-additional-info-panel">
                <Typography variant="subtitle2">Opportunity</Typography>
                {quoteLoading ? (
                  <Skeleton variant="rectangular" width="100%" height="40px" />
                ) : (
                  <div>
                    <CRMSearch
                      disabled={!editOpportunityInfo}
                      object="Opportunity"
                      value={selectedOpp}
                      label="Opportunity"
                      onChange={(e, value) => setSelectedOpp(value)}
                    />
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setOpenDialogs(prev => ({ ...prev, newOpportunity: true }))}
                    >
                      Create New Opportunity
                    </Button>
                  </div>
                )}
                <Divider />
                <div>
                  <Typography>Close Date</Typography>
                  {quoteLoading ? (
                    <Skeleton variant="text" width="10%" />
                  ) : (
                    <Typography>
                      {crmOpportunity?.CloseDate ? dayjs(crmOpportunity.CloseDate).format('MM/DD/YYYY') : '-'}
                    </Typography>
                  )}
                </div>
                <div>
                  <Typography>Stage</Typography>
                  {quoteLoading ? (
                    <Skeleton variant="text" width="10%" />
                  ) : (
                    <Typography>{crmOpportunity?.StageName ?? '-'}</Typography>
                  )}
                </div>
                <div>
                  <Typography>Forecast Category</Typography>
                  {quoteLoading ? (
                    <Skeleton variant="text" width="10%" />
                  ) : (
                    <Typography>{crmOpportunity?.ForecastCategoryName ?? '-'}</Typography>
                  )}
                </div>
                <div>
                  <Typography>Core Competency</Typography>
                  {quoteLoading ? (
                    <Skeleton variant="text" width="10%" />
                  ) : (
                    <Typography>{crmOpportunity?.Line_Of_Business__c ?? '-'}</Typography>
                  )}
                </div>
                <div>
                  <Typography>Set Asides</Typography>
                  {quoteLoading ? (
                    <Skeleton variant="text" width="10%" />
                  ) : (
                    <Typography>{crmOpportunity?.Set_Asides__c ?? '-'}</Typography>
                  )}
                </div>
                {quoteLoading ? (
                  <Skeleton variant="rectangular" width="64px" height="40px" />
                ) : editOpportunityInfo ? (
                  <div>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditOpportunityInfo(false)
                        setSelectedOpp(
                          quote.CRMOpportunity ? { id: quote.CRMOpportunity, label: crmOpportunity?.Name ?? '' } : null,
                        )
                      }}
                    >
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={handleEditOpportunityInfo}>
                      Save
                    </Button>
                  </div>
                ) : (
                  <Button variant="contained" onClick={() => setEditOpportunityInfo(true)}>
                    Edit
                  </Button>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
          <NewOpportunityDialog
            open={openDialogs.newOpportunity}
            quoteId={quoteId ?? ''}
            setCrmOpportunity={setCrmOpportunity}
            onClose={() => setOpenDialogs(prev => ({ ...prev, newOpportunity: false }))}
            setAccount={setSelectedAccount}
            setContact={setSelectedContact}
            setOpportunity={setSelectedOpp}
          />
          <Snackbar open={saveOpportunityError}>
            <Alert severity="error" variant="filled" onClose={() => setSaveOpportunityError(false)}>
              Opportunity information failed to update. Please try again or contact support if the problem persists.
            </Alert>
          </Snackbar>
        </>
      )}
      {activeTab === 'quotepdfs' && (
        <Accordion disableGutters defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>IMPRES Quotes</AccordionSummary>
          <AccordionDetails>
            <div className="quote-details-additional-info-panel">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Quote Name</TableCell>
                    <TableCell width="70px" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>TODO</TableCell>
                      <TableCell>
                        <Button variant="outlined">View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </AccordionDetails>
        </Accordion>
      )}
      <Fab
        variant="extended"
        color="primary"
        onClick={() => setOpenDialogs(prev => ({ ...prev, generateQuote: true }))}
      >
        Generate Quote
      </Fab>
      <GenerateQuoteDialog
        open={openDialogs.generateQuote}
        onClose={() => setOpenDialogs(prev => ({ ...prev, generateQuote: false }))}
        quoteId={quoteId ?? ''}
        quoteName={quote.QuoteNumber + 'v' + quote.Version + (quote.Name ? '-' + quote.Name : '')}
      />
    </div>
  )
}

export default QuoteDetails
